import { useReveal } from "@/hooks/useReveal";
import { Link } from "react-router-dom";

const services = [
  {
    title: "Custom Software Development",
    tagline: "Built for your exact needs",
    description: "Web applications, mobile apps, enterprise software, and cloud solutions crafted from scratch to match your business processes.",
    icon: "ri-code-s-slash-line",
    color: "#D4FF3F",
    features: ["Full-stack development", "API integrations", "Cloud deployment", "Ongoing maintenance"],
  },
  {
    title: "IT Consulting",
    tagline: "Smart technology decisions",
    description: "Strategic guidance to optimize your tech stack, modernize legacy systems, and implement tools that drive efficiency.",
    icon: "ri-lightbulb-flash-line",
    color: "#F7C59F",
    features: ["Tech stack audit", "Process optimization", "Digital transformation", "Security review"],
  },
  {
    title: "Digital Marketing",
    tagline: "Grow your audience",
    description: "Comprehensive marketing strategies including SEO, paid advertising, content marketing, and brand positioning.",
    icon: "ri-megaphone-line",
    color: "#E2724B",
    features: ["SEO & content strategy", "Paid advertising", "Social media management", "Analytics & reporting"],
  },
  {
    title: "Full-Cycle Delivery",
    tagline: "From idea to launch",
    description: "End-to-end project delivery: business analysis, UI/UX design, development, testing, launch, and post-launch support.",
    icon: "ri-rocket-line",
    color: "#D4FF3F",
    features: ["Business analysis", "UI/UX design", "QA & testing", "Post-launch support"],
  },
];

export default function Programs() {
  const ref = useReveal<HTMLDivElement>();

  return (
    <section ref={ref} id="services" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-[1320px] px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-ink/60">What we offer</span>
            <h2 className="mt-4 text-display text-4xl md:text-6xl leading-[1.05] text-balance max-w-2xl">
              Solutions that <span className="italic font-light">drive results</span>.
            </h2>
          </div>
          <Link
            to="/services"
            data-reveal
            data-reveal-delay="120"
            className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-3 text-xs uppercase tracking-widest2 cursor-pointer whitespace-nowrap hover:border-ink transition-colors"
          >
            View all services
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-base" />
            </span>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((s, i) => (
            <div
              key={s.title}
              data-reveal
              data-reveal-delay={`${i * 80}`}
              className="group relative rounded-3xl overflow-hidden bg-cream border border-ink/5 hover:border-ink/15 transition-all cursor-default p-8 md:p-10"
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-2xl"
                  style={{ backgroundColor: s.color }}
                >
                  <i className={`${s.icon} text-xl text-ink`} />
                </div>
                <span className="text-[10px] uppercase tracking-widest2 text-ink/40 font-semibold">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>

              <h3 className="text-display text-2xl md:text-3xl leading-tight mb-2">{s.title}</h3>
              <p className="text-sm text-ink/60 italic mb-4">{s.tagline}</p>
              <p className="text-ink/70 text-sm leading-relaxed mb-6">{s.description}</p>

              <div className="flex flex-wrap gap-2">
                {s.features.map((f) => (
                  <span
                    key={f}
                    className="rounded-full bg-ink/5 text-ink/70 px-3 py-1.5 text-[11px] uppercase tracking-widest2"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}